// Chunked upload for very large files (500k+ row master lists).
//
// Why: Cloudflare Free/Pro caps single requests at 60-100s + 100 MB. A 50 MB
// raw CSV (even after gzip) can hit the timeout if the user's network is slow.
// Splitting into 4 MB chunks lets each upload finish well under 30 s, and any
// chunk that fails can be retried independently without re-sending the rest.
//
// Flow:
//   1. POST /admin/uploads/init      → { upload_id }
//   2. POST /admin/uploads/{id}/chunk?index=0..N (one per chunk, sequential)
//   3. POST /admin/uploads/{id}/finalize-master?brand=seat → { job_id }
//   4. Front-end polls /admin/upload-jobs/{job_id} as before.

import { api } from "./api";
import { maybeGzipFile } from "./upload";

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB — well below CF 100 s and 100 MB caps
const USE_CHUNK_THRESHOLD = 4 * 1024 * 1024; // anything >4 MB uses chunks

/**
 * Upload a master list and start the background import job.
 * @param {File} file  The user-selected file.
 * @param {string|undefined} brand  Selected brand (audi/vw/skoda/seat/bmw/mercedes).
 * @param {(p: {phase:string, percent:number}) => void} onProgress  UI callback.
 * @param {{ finalizeKind?: "master"|"mercedes", brandIsFixed?: boolean }} [opts]
 *   When finalizeKind === "mercedes", the chunks are finalized via the Mercedes
 *   import path (which forces brand=mercedes and expects a 3-column XLSX).
 *   brandIsFixed=true suppresses the `?brand=` query param for Mercedes.
 * @returns {Promise<{job_id: string}>}
 */
export async function uploadMasterChunked(file, brand, onProgress = () => {}, opts = {}) {
  const finalizeKind = opts.finalizeKind || "master";
  const brandIsFixed = !!opts.brandIsFixed;
  const finalizeUrl = (uid) => {
    if (finalizeKind === "mercedes") return `/admin/uploads/${uid}/finalize-mercedes`;
    if (finalizeKind === "bmw") return `/admin/uploads/${uid}/finalize-bmw`;
    return `/admin/uploads/${uid}/finalize-master`;
  };
  const singleShotUrl = null;

  // Step 1: compress in browser (transparent for both single-shot and chunked).
  onProgress({ phase: "compress", percent: 0 });
  const gz = await maybeGzipFile(file);

  // Step 2: pick path — small files keep the simple single-shot route.
  if (gz.size < USE_CHUNK_THRESHOLD && singleShotUrl) {
    const fd = new FormData();
    fd.append("file", gz);
    const params = !brandIsFixed && brand ? { brand } : undefined;
    onProgress({ phase: "upload", percent: 50 });
    const { data } = await api.post(singleShotUrl, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      params,
      timeout: 0,
    });
    onProgress({ phase: "done", percent: 100 });
    return data;
  }

  // Step 3: chunked path.
  const totalChunks = Math.ceil(gz.size / CHUNK_SIZE);
  let uploadId;
  try {
    const { data: init } = await api.post("/admin/uploads/init", {
      filename: gz.name,
      total_chunks: totalChunks,
      total_bytes: gz.size,
    });
    uploadId = init.upload_id;
  } catch (err) {
    // Backend deploy lag — chunked endpoints might not be live yet. Fall back
    // to single-shot upload so the user never sees a "Not Found" failure when
    // the frontend has been deployed ahead of the backend.
    if (err?.response?.status === 404 && singleShotUrl) {
      const fd = new FormData();
      fd.append("file", gz);
      const params = !brandIsFixed && brand ? { brand } : undefined;
      onProgress({ phase: "upload", percent: 50 });
      const { data } = await api.post(singleShotUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
        timeout: 0,
      });
      onProgress({ phase: "done", percent: 100 });
      return data;
    }
    throw err;
  }

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, gz.size);
    const slice = gz.slice(start, end);

    const fd = new FormData();
    fd.append("chunk", slice, `${gz.name}.part${i}`);

    // Up to 3 retries per chunk — the server treats same-index repost as
    // idempotent replacement, so network blips are recoverable.
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await api.post(`/admin/uploads/${uploadId}/chunk`, fd, {
          params: { index: i },
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        lastErr = null;
        break;
      } catch (err) {
        lastErr = err;
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    if (lastErr) throw lastErr;

    onProgress({
      phase: "upload",
      percent: Math.round(((i + 1) / totalChunks) * 95),
    });
  }

  // Step 4: finalize — server reassembles, kicks off background job.
  onProgress({ phase: "finalize", percent: 96 });
  const finalizeParams = {};
  if (!brandIsFixed && brand) finalizeParams.brand = brand;
  // Mercedes variant (pkw / lkw / combined) — only meaningful for finalize-mercedes
  if (finalizeKind === "mercedes" && opts.variant) finalizeParams.variant = opts.variant;
  // Mercedes price column currency (EUR | HUF). HUF tells the worker to
  // divide each cell by the admin currency_huf_rate before storing.
  if (finalizeKind === "mercedes" && opts.priceCurrency) finalizeParams.price_currency = opts.priceCurrency;
  const { data: fin } = await api.post(
    finalizeUrl(uploadId),
    null,
    { params: Object.keys(finalizeParams).length ? finalizeParams : undefined, timeout: 60000 },
  );
  onProgress({ phase: "done", percent: 100 });
  return fin;
}
